import React, { useState, useEffect } from 'react';
import { FaExternalLinkAlt, FaImage, FaInfoCircle, FaGlobe, FaLink } from 'react-icons/fa';
import adiParseService from '../../services/adiParseService';

export const NetworkWebContent = ({ network }) => {
  const [webData, setWebData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCancelled, setIsCancelled] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchWebContent = async () => {
      // Reset states when network changes
      setIsCancelled(false);
      setWebData(null);
      setError(null);

      if (!network?.code) {
        setError('Network identity not available');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const data = await adiParseService.getAllMainData(network.code, abortController);

        // Check if component is still mounted and data is valid
        if (!abortController.signal.aborted) {
          if (data) {
            setWebData(data);
          } else {
            // Data is null - this means request was cancelled
            setIsCancelled(true);
          }
        }
      } catch (err) {
        // Only set error if request wasn't cancelled
        if (!abortController.signal.aborted) {
          console.error('Error fetching web content:', err);
          setError(err.message || 'Failed to load web content');
        }
      } finally {
        // Always update loading state to false when request completes
        setLoading(false);
      }
    };

    fetchWebContent();

    // Cleanup function
    return () => {
      abortController.abort();
      setIsCancelled(true);
    };
  }, [network?.code]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-success mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading web content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-warning" role="alert" style={{
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        border: '1px solid rgba(245, 158, 11, 0.2)',
        borderRadius: '12px'
      }}>
        <FaInfoCircle className="me-2" />
        <strong>Operate Components Unavailable:</strong> {error}
      </div>
    );
  }

  // Show "no data" message if we have webData but hasData is false
  if (webData && !webData.hasData) {
    return (
      <div className="alert alert-info" role="alert" style={{
        background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
        border: '1px solid rgba(59, 130, 246, 0.2)',
        borderRadius: '12px'
      }}>
        <FaInfoCircle className="me-2" />
        No web content data available for this network.
      </div>
    );
  }

  // If cancelled or no data yet, show loading
  if (!webData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-success mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading web content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header Section with Background Image */}
      {webData.sectionMain1Background2ImageUrl && (
        <div style={{
          height: '300px',
          backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${webData.sectionMain1Background2ImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '30px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div className="text-center text-white">
            <h1 style={{ fontSize: '3rem', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
              {webData.networkName}
            </h1>
            <p style={{ fontSize: '1.2rem', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
              Business Network - DID Components
            </p>
          </div>
        </div>
      )}

      <div className="row g-4">
        {/* Logo Section - Moved to top and improved styling */}
        {webData.logoUrl && (
          <div className="col-12">
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)',
              borderRadius: '16px',
              padding: '40px',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              border: '1px solid rgba(148, 163, 184, 0.2)'
            }}>
              {/* Subtle pattern overlay for transparency support */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(16, 185, 129, 0.05) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(59, 130, 246, 0.05) 0%, transparent 50%)',
                pointerEvents: 'none'
              }}></div>
              <img
                src={webData.logoUrl}
                alt={`${webData.networkName} Logo`}
                style={{
                  maxHeight: '120px',
                  maxWidth: '300px',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))',
                  position: 'relative',
                  zIndex: 1
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div style={{
                display: 'none',
                color: '#64748b',
                fontStyle: 'italic',
                textAlign: 'center',
                position: 'relative',
                zIndex: 1,
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.1rem'
              }}>
                <FaImage style={{ marginRight: '8px' }} />
                Logo not available
              </div>
            </div>
          </div>
        )}

        {/* External Links Section - Moved above description */}
        <div className="col-12">
          <div style={{
            background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid rgba(139, 92, 246, 0.2)'
          }}>
            <h5 style={{
              fontSize: '1.1rem',
              fontWeight: '600',
              color: '#5b21b6',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FaGlobe style={{ color: '#8b5cf6' }} />
              External Resources
            </h5>
            <div className="row g-3">
              {/* BankOnLedger Link */}
              <div className="col-md-6">
                <a
                  href={webData.bankOnLedgerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'block',
                    textDecoration: 'none',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    padding: '16px 20px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '1rem' }}>BankOnLedger</div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>View on BankOnLedger.com</div>
                    </div>
                    <FaExternalLinkAlt />
                  </div>
                </a>
              </div>

              {/* Qoboto Link */}
              <div className="col-md-6">
                <a
                  href={webData.qobotoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'block',
                    textDecoration: 'none',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    padding: '16px 20px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '1rem' }}>Qoboto</div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>View on Qoboto.com</div>
                    </div>
                    <FaExternalLinkAlt />
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Description Section - Now full width */}
        <div className="col-12">
          <div style={{
              background: 'linear-gradient(135deg, #f8f8f9ff 0%, #d7e5fdff 100%)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid rgba(245, 158, 11, 0.2)'
          }}>
            <h5 style={{
              fontSize: '1.1rem',
              fontWeight: '600',
              color: '#92400e',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FaInfoCircle style={{ color: '#064e0bff' }} />
              Network Description
            </h5>
            <div style={{
              background: 'linear-gradient(135deg, #f8f8f9ff 0%, #d7e5fdff 100%)',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid rgba(245, 158, 11, 0.1)',
              minHeight: '120px' 
            }}>
              {webData.sectionMain1Description ? (
                <div
                  style={{
                    color: '#374151',
                    lineHeight: '1.6',
                    margin: 0,
                    fontSize: '1rem'
                  }}
                  dangerouslySetInnerHTML={{
                    __html: webData.sectionMain1Description.replace(/�/g, '').replace(/[\u0000-\u001F\u007F-\u009F]/g, '')
                  }}
                />
              ) : (
                <p style={{
                  color: '#6b7280',
                  fontStyle: 'italic',
                  margin: 0
                }}>
                  No description available
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Network Identity Info */}
        <div className="col-12">
          <div style={{
            background: 'linear-gradient(135deg, #f3f4f6 0%, #1848a9ff 100%)',
            borderRadius: '16px',
            padding: '20px',
            border: '1px solid rgba(107, 114, 128, 0.2)'
          }}>
            <h6 style={{
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FaLink style={{ color: '#6b7280' }} />
              Network Identity Information
            </h6>
            <div className="row g-2">
              <div className="col-md-6">
                <small className="text-muted d-block">Network Name</small>
                <code style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid rgba(107, 114, 128, 0.2)',
                  display: 'block',
                  fontSize: '0.85rem'
                }}>
                  {webData.networkName}
                </code>
              </div>
              <div className="col-md-6">
                <small className="text-muted d-block">Original Identity</small>
                <code style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid rgba(107, 114, 128, 0.2)',
                  display: 'block',
                  fontSize: '0.85rem',
                  wordBreak: 'break-all'
                }}>
                  {network.code || 'N/A'}
                </code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};